import styled, { css } from "styled-components";
import { FC } from "react";

import Text from "@/components/Text";
import { ReleaseCompilationTypeEnum } from "@/utils/enums";

interface BadgeProps {
  $type?: ReleaseCompilationTypeEnum;
}

const Badge = styled.div<BadgeProps>`
  display: inline-block;
  padding: 6px 11px;
  padding-left: 19px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
  ${Text} {
    font-weight: 700;
    font-size: 11px;
    line-height: 15px;
    position: relative;
    color: ${({ theme }) => theme.colors.gray};
    &:before {
      content: "";
      width: 4px;
      height: 4px;
      left: -8px;
      background-color: ${({ theme }) => theme.colors.gray};
      top: calc(50% - 2px);
      border-radius: 50%;
      position: absolute;
    }
  }
  ${({ $type }) =>
    !$type &&
    css`
      background: ${({ theme }) => theme.colors.grayscaleLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.gray};
        &:before {
          background: ${({ theme }) => theme.colors.gray};
        }
      }
    `}
  ${({ $type }) =>
    $type === ReleaseCompilationTypeEnum.FRESH &&
    css`
      background: ${({ theme }) => theme.colors.distributionLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.distribution};
        &:before {
          background: ${({ theme }) => theme.colors.distribution};
        }
      }
    `}
  ${({ $type }) =>
    $type === ReleaseCompilationTypeEnum.REDISTRIBUTION &&
    css`
      background: ${({ theme }) => theme.colors.distributionPurpleLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.distributionPurple};
        &:before {
          background: ${({ theme }) => theme.colors.distributionPurple};
        }
      }
    `}
  ${({ $type }) =>
    $type === ReleaseCompilationTypeEnum.DISTRIBUTION &&
    css`
      background: ${({ theme }) => theme.colors.optimisationLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.optimisation};
        &:before {
          background: ${({ theme }) => theme.colors.optimisation};
        }
      }
    `}
  ${({ $type }) =>
    $type === ReleaseCompilationTypeEnum.REUSE &&
    css`
      background: ${({ theme }) => theme.colors.researchLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.research};
        &:before {
          background: ${({ theme }) => theme.colors.research};
        }
      }
    `}
    ${({ $type }) =>
    $type === ReleaseCompilationTypeEnum.MODIFIED_REDISTRIBUTION &&
    css`
      background: ${({ theme }) => theme.colors.postProductionLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.postProduction};
        &:before {
          background: ${({ theme }) => theme.colors.postProduction};
        }
      }
    `}
    ${({ $type }) =>
    $type === ReleaseCompilationTypeEnum.REDISTRIBUTION_PREDICTED &&
    css`
      background: #f1eff1;
      ${Text} {
        color: #513252;
        &:before {
          background: #513252;
        }
      }
    `}
`;

const renderBadgeTitle = (type?: ReleaseCompilationTypeEnum) => {
  switch (type) {
    case ReleaseCompilationTypeEnum.REUSE:
      return "Reuse";
    case ReleaseCompilationTypeEnum.FRESH:
      return "Fresh";
    case ReleaseCompilationTypeEnum.DISTRIBUTION:
      return "Distribution";
    case ReleaseCompilationTypeEnum.REDISTRIBUTION:
      return "Redistribution";
    case ReleaseCompilationTypeEnum.MODIFIED_REDISTRIBUTION:
      return "Modified Redistribution";
    case ReleaseCompilationTypeEnum.REDISTRIBUTION_PREDICTED:
      return "Redistribution / Predicted";
    default:
      return "No Type";
  }
};

interface Props {
  type?: ReleaseCompilationTypeEnum;
  className?: string;
}

const DistributionTypeBadge: FC<Props> = ({ type, className }) => {
  return (
    <Badge className={className} $type={type}>
      <Text $type="smallHeadline">{renderBadgeTitle(type)}</Text>
    </Badge>
  );
};

export default DistributionTypeBadge;
